import _ from 'lodash';
import React, { useMemo, useState, ChangeEvent, useCallback } from 'react';

// Replace 'CustomTable' with the name of your custom table component
import Card from '@mui/material/Card';
import Stack from '@mui/system/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { useTranslate } from 'src/locales';
import { useAppDispatch } from 'src/store/store';
import { EmploymentType } from 'src/services/jobs/jobs.types';
import { openPlacementEditor } from 'src/store/slices/jobs/jobsSlice';
import {
  useGetPlacementsQuery
} from 'src/services/applications/applications.service';

import Scrollbar from 'src/components/scrollbar';
import {
  useTable,
  TableNoData,
  TableSkeleton,
  TableHeadCustom,
  TablePaginationCustom,
} from 'src/components/table';

import PlacementsTableRow from 'src/sections/candidates/placements/placements-table-row';
import PlacementsTableToolbar from 'src/sections/candidates/placements/placements-table-toolbar';

import { TenantType } from 'src/types/enums';

interface ApplicationPlacementsProps {

}

const DEFAULT_PAGE = {
  page: 1,
  per_page: 10,
};

// Maps a sortable column id to the backend field it sorts on
const SORT_FIELD_MAP: Record<string, string> = {
  candidate_name: 'candidate_first_name',
  job_title: 'job_title',
  client: 'employer_company_name',
  recruiter: 'recruiter_company_name',
  employment_type: 'employment_type',
  start_date: 'start_date',
  end_date: 'end_date',
  owner: 'recruiter_first_name',
};

const PlacementsTable: React.FC<ApplicationPlacementsProps> = () => {

  const { t } = useTranslate();

  const tenantType = useOrgTenant();

  const dispatch = useAppDispatch();

  const tableLabels = useMemo(
    () => [
      {
        id: 'candidate_name',
        label: t('jobs.detail.placements.table_labels.candidate_name'),
      },
      {
        id: 'job_title',
        label: t('jobs.detail.placements.table_labels.job_title'),
      },
      ...(tenantType === TenantType.Recruiter ? [{
        id: 'client',
        label: t('jobs.detail.placements.table_labels.client'),
      }] : []),
      ...(tenantType === TenantType.Client ? [{
        id: 'recruiter',
        label: t('jobs.detail.placements.table_labels.recruiter'),
      }] : []),
      {
        id: 'employment_type',
        label: t('jobs.detail.placements.table_labels.type'),
      },
      {
        id: 'start_date',
        label: t('jobs.detail.placements.table_labels.start_date'),
      },
      {
        id: 'end_date',
        label: t('jobs.detail.placements.table_labels.end_date'),
      },
      {
        id: 'owner',
        label: t('jobs.detail.placements.table_labels.owner'),
      },
      {
        id: ''
      },
    ],
    [t, tenantType]
  );

  const [filterQuery, setFilterQuery] = useState<string>('');
  const [employmentTypes, setEmploymentTypes] = useState<EmploymentType[]>([]);
  const [owners, setOwners] = useState<string[]>([]);
  const [activeOnly, setActiveOnly] = useState<boolean>(false);
  const [pageOptions, setPageOptions] = useState(DEFAULT_PAGE);

  const table = useTable();

  const sortBy = SORT_FIELD_MAP[table.orderBy];

  const { currentData: placementData, isLoading } = useGetPlacementsQuery({
    pageOptions,
    searchQuery: filterQuery,
    employmentTypes,
    owners,
    activeOnly,
    sortBy,
    sortOrder: sortBy ? table.order : undefined,
  });

  const handleSort = useCallback((id: string) => {
    table.onSort(id);
    setPageOptions((prev) => ({ ...prev, page: 1 }));
  }, [table]);

  const handlePageChange = useCallback(
    (_event: any, pageNumber: number) => {
      setPageOptions((prev) => ({ ...prev, page: pageNumber++ }));
    },
    [setPageOptions]
  );

  const handleRowsPerPageChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setPageOptions((prev) => ({ ...prev, per_page: +event.target.value }));
    },
    [setPageOptions]
  );

  const debouncedSearch = useMemo(
    () => _.debounce((value: string) => {
      setFilterQuery(value);
      setPageOptions((prev) => ({ ...prev, page: 1 }));
    }, 500),
    []
  );

  const handleFilters = useCallback((filter: string, value: any) => {
    if (filter === 'search_query') {
      debouncedSearch(value as string);
      return;
    }

    if (filter === 'employment_type') {
      setEmploymentTypes(value as EmploymentType[]);
    } else if (filter === 'owner') {
      setOwners(value as string[]);
    } else if (filter === 'active') {
      setActiveOnly(Boolean(value));
    }

    setPageOptions((prev) => ({
      ...prev,
      page: 1, // Reset to first page on filter change
    }));
  }, [debouncedSearch]);

  const clearFilters = useCallback(() => {
    setFilterQuery('');
    setEmploymentTypes([]);
    setOwners([]);
    setActiveOnly(false);
    setPageOptions((prev) => ({ ...prev, page: 1 }));
  }, []);

  const handleEditPlacement = useCallback((placementId: string) => {
    dispatch(openPlacementEditor({ placementId }));
  }, [dispatch]);

  const notFound = !placementData || !placementData.count;

  return (
    <Card>

      {
        isLoading ? (
          <TableSkeleton />
        )
          : (
            <>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <PlacementsTableToolbar
                  table={table}
                  onFilters={handleFilters}
                  clearFilters={clearFilters}
                />
              </Stack>

              <TableContainer>
                <Scrollbar>
                  <Table sx={{ minWidth: 680 }} size={table?.dense ? 'small' : 'medium'}>
                    <TableHeadCustom
                      headLabel={tableLabels}
                      order={table.order}
                      orderBy={table.orderBy}
                      onSort={handleSort}
                    />
                    <TableBody>
                      {placementData?.results.map((row) => (
                        <PlacementsTableRow key={row.id} row={row} onEditPlacement={handleEditPlacement} />
                      )) ?? []}

                      <TableNoData notFound={notFound} />
                    </TableBody>
                  </Table>
                </Scrollbar>
                <TablePaginationCustom
                  count={placementData?.count ?? 0}
                  page={pageOptions.page}
                  rowsPerPage={pageOptions.per_page}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  dense={table?.dense}
                  onChangeDense={table?.onChangeDense}
                />
              </TableContainer>
            </>
          )
      }
    </Card>
  );
};


export default PlacementsTable;
